import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ServiceBillingBadge } from "@/components/billing/ServiceBillingBadge";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  ADMISSION_STATUS_LABELS,
  EMERGENCY_ARRIVAL_LABELS,
  TRIAGE_SHORT,
  type Admission,
} from "@/core/admissions/types";
import { billingSnapshotForAdmission } from "@/core/billing/service-billing-status";
import { getPatientById, patientDetailPath } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { listAmbulanceTransports } from "@/core/services/service-admission-queries";
import { routes } from "@/router/all_routes";

export function AmbulanceTransportsPage() {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:admissions", refresh);
    window.addEventListener("hospitol:billing-documents", refresh);
    return () => {
      window.removeEventListener("hospitol:admissions", refresh);
      window.removeEventListener("hospitol:billing-documents", refresh);
    };
  }, []);

  const transports = useMemo(() => {
    void refreshKey;
    return listAmbulanceTransports();
  }, [refreshKey]);

  const pagination = useClientPagination(transports, [], 25);
  const { pagedItems } = pagination;

  return (
    <div>
      <PageHeader
        title="Transports ambulance"
        description="Arrivées SMUR / ambulance aux urgences — gestion et facturation transport."
        actions={
          <div className="ph-page-header-actions">
            <Link to={routes.emergency} className="btn btn-outline-secondary btn-sm">
              Urgences
            </Link>
          </div>
        }
      />

      <div className="ph-invoice-sheet mb-3">
        <p className="small text-muted mb-3">
          Liste des passages urgences enregistrés avec mode d&apos;arrivée &quot;Ambulance / SMUR&quot;.
          Le forfait transport est facturé automatiquement (paramètres établissement).
        </p>
        {transports.length === 0 ? (
          <p className="text-muted small mb-0">
            Aucun transport ambulance — créez une admission urgence avec arrivée ambulance.
          </p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table mb-0">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Triage</th>
                  <th>Motif</th>
                  <th>Statut</th>
                  <th>Arrivée</th>
                  <th>Facturation transport</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((adm: Admission) => {
                  const patient = getPatientById(adm.patientId);
                  const billing = billingSnapshotForAdmission(adm.id, "ambulance");
                  return (
                    <tr key={adm.id} className="ph-diag-row">
                      <td data-label="Patient" className="fw-medium">
                        {patient ? (
                          <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
                        ) : (
                          adm.walkInName || "—"
                        )}
                      </td>
                      <td data-label="Triage">
                        {adm.triageLevel ? (
                          <span className={`hosp-triage-badge hosp-triage-badge--${adm.triageLevel}`}>
                            {TRIAGE_SHORT[adm.triageLevel]}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td data-label="Motif" className="small">
                        {adm.motif}
                      </td>
                      <td data-label="Statut">
                        <span className="badge text-bg-light border">
                          {ADMISSION_STATUS_LABELS[adm.status]}
                        </span>
                      </td>
                      <td data-label="Arrivée" className="small text-muted">
                        {adm.arrivalMode
                          ? EMERGENCY_ARRIVAL_LABELS[adm.arrivalMode]
                          : "—"}
                        <br />
                        {new Date(adm.createdAt).toLocaleString("fr-FR")}
                      </td>
                      <td data-label="Facturation">
                        <ServiceBillingBadge snapshot={billing} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>
    </div>
  );
}
